import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import {
  Button,
  ConfirmDialog,
  DataTable,
  Drawer,
  EmptyState,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { settingsApi } from '../settings/settingsApi';
import { budgetsApi } from './budgetsApi';
import BudgetForm from './BudgetForm';
import './budgets.css';

export default function BudgetsPage() {
  const t = useT();
  const toast = useToast();
  const { properties, hasPermission } = useProperty();
  const canManage = hasPermission('budgets.manage');
  const canManageSettings = hasPermission('settings.manage');

  // Organization-wide enforcement: Warn (default) surfaces guidance only; Block refuses
  // over-budget submissions/approvals.
  const [enforcement, setEnforcement] = useState('Warn');
  const [enforcementSaving, setEnforcementSaving] = useState(false);

  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [costCenters, setCostCenters] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [currencies, setCurrencies] = useState(['USD']);

  const [filterProperty, setFilterProperty] = useState('');
  const [filterCostCenter, setFilterCostCenter] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const propName = useCallback(
    (id) => properties.find((p) => p.id === id)?.name || (id ? id : t('Organization')),
    [properties, t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await budgetsApi.list({
        propertyId: filterProperty || undefined,
        costCenter: filterCostCenter || undefined,
      });
      setBudgets(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(t('Could not load budgets'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [filterProperty, filterCostCenter, toast, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const eff = await settingsApi.getEffective();
        setCostCenters((eff?.costCenters || []).filter((c) => c.isActive !== false));
        setGlAccounts((eff?.glAccounts || []).filter((g) => g.isActive !== false));
        setCurrencies(eff?.currencies?.length ? eff.currencies : ['USD']);
      } catch {
        // Non-fatal; the form falls back to empty lists / USD.
      }
    })();
  }, []);

  useEffect(() => {
    if (!canManageSettings) return;
    (async () => {
      try {
        const org = await settingsApi.getOrg();
        setEnforcement(org?.budgetEnforcementMode === 'Block' ? 'Block' : 'Warn');
      } catch {
        // Non-fatal; defaults to Warn.
      }
    })();
  }, [canManageSettings]);

  const changeEnforcement = async (mode) => {
    const previous = enforcement;
    setEnforcement(mode);
    setEnforcementSaving(true);
    try {
      const res = await settingsApi.updateOrgBudgetEnforcement({ budgetEnforcementMode: mode });
      setEnforcement(res?.budgetEnforcementMode === 'Block' ? 'Block' : 'Warn');
      toast.success(mode === 'Block'
        ? t('Budget enforcement set to block over-budget documents')
        : t('Budget enforcement set to warn only'));
    } catch (err) {
      // Roll the select back so it never shows a mode the server didn't accept.
      setEnforcement(previous);
      toast.error(t('Could not update budget enforcement'), { description: err?.message });
    } finally {
      setEnforcementSaving(false);
    }
  };

  const openNew = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (b) => { setEditing(b); setDrawerOpen(true); };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (editing) {
        await budgetsApi.update(editing.id, { ...payload, id: editing.id });
        toast.success(t('Budget updated'));
      } else {
        await budgetsApi.create(payload);
        toast.success(t('Budget created'));
      }
      setDrawerOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(t('Could not save budget'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await budgetsApi.remove(deleteTarget.id);
      toast.success(t('Budget deleted'));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(t('Could not delete budget'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const ccFilterOptions = useMemo(
    () => [{ value: '', label: t('All cost centres') }, ...costCenters.map((c) => ({ value: c.code, label: c.name ? `${c.code} · ${c.name}` : c.code }))],
    [costCenters, t],
  );
  const propFilterOptions = useMemo(
    () => [{ value: '', label: t('All properties') }, ...properties.map((p) => ({ value: p.id, label: p.name }))],
    [properties, t],
  );

  const money = (n, cur) => `${cur || 'USD'} ${formatAmount(n)}`;

  const columns = [
    {
      key: 'costCenter',
      header: t('Cost centre'),
      width: 150,
      render: (b) => (
        <div>
          <div style={{ fontWeight: 600 }}>{b.costCenter}</div>
          <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>
            {b.glAccountCode ? `GL ${b.glAccountCode}` : t('Whole cost centre')}
          </div>
        </div>
      ),
    },
    { key: 'property', header: t('Property'), width: 150, render: (b) => propName(b.propertyId) },
    { key: 'period', header: t('Period'), width: 120, render: (b) => b.periodLabel },
    { key: 'allocated', header: t('Allocated'), align: 'right', width: 130, render: (b) => money(b.amount?.amount, b.amount?.currency) },
    { key: 'committed', header: t('Committed'), align: 'right', width: 130, render: (b) => <span className="bud-num">{money(b.committed, b.amount?.currency)}</span> },
    { key: 'actual', header: t('Actual'), align: 'right', width: 120, render: (b) => <span className="bud-num">{money(b.actual, b.amount?.currency)}</span> },
    {
      key: 'remaining',
      header: t('Remaining'),
      align: 'right',
      width: 130,
      render: (b) => {
        const over = Number(b.remaining) < 0;
        return <span className={`bud-num ${over ? 'bud-num--over' : ''}`}>{money(b.remaining, b.amount?.currency)}</span>;
      },
    },
    {
      key: 'used',
      header: t('% used'),
      width: 160,
      render: (b) => {
        const allocated = Number(b.amount?.amount || 0);
        const consumed = Number(b.consumed || 0);
        const pct = allocated > 0 ? (consumed / allocated) * 100 : 0;
        const over = pct > 100;
        const fill = over ? 'is-over' : pct >= 85 ? 'is-warning' : '';
        return (
          <div className="bud-meter">
            <div className="bud-meter__top">
              <span className={`bud-pct ${over ? 'bud-pct--over' : ''}`}>{pct.toFixed(0)}%</span>
            </div>
            <div className="bud-bar">
              <div className={`bud-bar__fill ${fill}`} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
          </div>
        );
      },
    },
    ...(canManage
      ? [{
          key: 'actions',
          header: '',
          width: 90,
          render: (b) => (
            <div className="pp-row-actions" onClick={(e) => e.stopPropagation()}>
              <IconButton aria-label={t('Edit budget')} variant="ghost" size="icon-sm" onClick={() => openEdit(b)}><Icon.Pencil size={15} /></IconButton>
              <IconButton aria-label={t('Delete budget')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(b)}><Icon.Trash size={15} /></IconButton>
            </div>
          ),
        }]
      : []),
  ];

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Budgets')}
        description={enforcement === 'Block'
          ? t('Period budgets by property and cost centre. Over-budget requisitions, orders and invoices are blocked.')
          : t('Period budgets by property and cost centre. Spend on coded requisitions and purchase orders is checked against these — warn-only.')}
        icon={<Icon.Wallet size={22} />}
        actions={canManage ? (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openNew}>{t('New budget')}</Button>
        ) : null}
      />

      <div className="pp-toolbar">
        <div style={{ minWidth: 220 }}>
          <Select value={filterProperty} onChange={setFilterProperty} options={propFilterOptions} ariaLabel={t('Filter by property')} searchable />
        </div>
        <div style={{ minWidth: 220 }}>
          <Select value={filterCostCenter} onChange={setFilterCostCenter} options={ccFilterOptions} ariaLabel={t('Filter by cost centre')} searchable />
        </div>
        {canManageSettings && (
          <div style={{ minWidth: 260, marginLeft: 'auto' }}>
            <Select
              value={enforcement}
              onChange={changeEnforcement}
              disabled={enforcementSaving}
              ariaLabel={t('Budget enforcement')}
              options={[
                { value: 'Warn', label: t('Enforcement: warn only') },
                { value: 'Block', label: t('Enforcement: block over-budget') },
              ]}
            />
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}><Spinner size={24} /></div>
      ) : (
        <DataTable
          columns={columns}
          data={budgets}
          rowKey={(row) => row.id}
          density="comfortable"
          onRowClick={canManage ? openEdit : undefined}
          emptyState={
            <EmptyState
              icon={<Icon.Wallet size={28} />}
              title={t('No budgets yet')}
              description={t('Define a budget for a cost centre and period to start tracking spend against it.')}
              action={canManage ? (
                <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openNew}>{t('New budget')}</Button>
              ) : null}
            />
          }
        />
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => (submitting ? null : (setDrawerOpen(false), setEditing(null)))}
        title={editing ? t('Edit budget') : t('New budget')}
        description={t('Set the cost centre, period and allocated amount. Optionally scope to a single GL account.')}
        size="md"
      >
        {drawerOpen && (
          <BudgetForm
            budget={editing}
            properties={properties}
            costCenters={costCenters}
            glAccounts={glAccounts}
            currencies={currencies}
            submitting={submitting}
            onSubmit={handleSubmit}
            onCancel={() => { setDrawerOpen(false); setEditing(null); }}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('Delete budget')}
        description={deleteTarget ? t('Delete the {costCenter} budget for {period}? This cannot be undone.', { costCenter: deleteTarget.costCenter, period: deleteTarget.periodLabel }) : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
      />
    </PageContainer>
  );
}
